import {
  Button,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  TextField,
} from "@mui/material";
import { debounce } from "@mui/material";
import { Box, Card } from "@mui/material";
import {
  IAsyncDropdownModel,
  IDropdownModel,
} from "common/models/IDropdownModel";
import asyncRequests from "common/requests/asyncRequests";
import DatePickerElement from "components/DatePickerElement";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { SelectInstance } from "react-select";
import AsyncSelect from "react-select/async";
import {
  FineReportsCount,
  IFineReportsFilter,
  IFineReportsFilterSchema,
} from "../api/types";
import { zodResolver } from "@hookform/resolvers/zod";
import MainCard from "components/MainCard";
import { CloudDownloadOutlined } from "@ant-design/icons";
import useFineStore from "../store/FineStore";
import { ReportsCount } from "../api/hooks";
import api from "utils/api";
import useAuth from "hooks/useAuth";
import { GetInstituteByLibrary } from "features/institute/api/hooks";
import { GetDepartmentValuesByInstitute } from "features/department/api/hooks";
import { SelectField } from "components/@extended/SelectField";
import { GetMemberTypeValues } from "features/memberType/api/hooks";
import { GetMaterialTypeValues } from "features/materialtype/api/hooks";
import { GetAdmissionYearValues } from "features/admissionYear/api/hooks";
import RadioButtonGroup from "components/RadioButtonGroup";
import dayjs from "dayjs";
import { showError } from "utils/showMessages";

const FineReports = () => {
  const apiURL = "api/Reports/fineReports";
  const fileName = "FineReports";
  const { modules } = useAuth();
  const [actualAmountLoading, setActualAmountLoading] =
    useState<boolean>(false);
  const [outStandingAmountLoading, setOutStandingAmountLoading] =
    useState<boolean>(false);
  const [waivedAmountLoading, setWaivedAmountLoading] =
    useState<boolean>(false);
  const [receivedAmountLoading, setReceivedAmountLoading] =
    useState<boolean>(false);

  const [data, setData] = useState<FineReportsCount>();

  const { reportsCountModel, handleReportsCountFiltering } = useFineStore();
  const methods = useForm<IFineReportsFilter>({
    mode: "all",
    resolver: zodResolver(IFineReportsFilterSchema),
  });

  const {
    register,
    reset,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = methods;

  const fromDate = watch("fromDate");
  const toDate = watch("toDate");

  useEffect(() => {
    if (fromDate && toDate) {
      const from = dayjs(fromDate);
      const to = dayjs(toDate);
      if (from.isValid() && to.isValid() && from.isAfter(to, "day")) {
        setValue("toDate", fromDate);
      }
    }
  }, [fromDate, toDate, setValue]);

  const onSubmit = handleSubmit(async (data) => {
    if (data.fromDate && data.toDate) {
      const from = dayjs(data.fromDate);
      const to = dayjs(data.toDate);
      if (from.isValid() && to.isValid() && from.isAfter(to, "day")) {
        showError("To Date must be greater than or equal to From Date");
        return;
      }
    }

    if (data.accessionNoValue) {
      data.materialAccessionNoID = String(data?.accessionNoValue.value);
    } else {
      data.materialAccessionNoID = null;
    }
    if (data.memberCodeValue) {
      data.memberID = String(data?.memberCodeValue.value);
    } else {
      data.memberID = null;
    }

    data.waivedAmountType = data.amountFilterType === "waived" ? 1 : null;
    data.receivedAmountType = data.amountFilterType === "received" ? 1 : null;
    data.outstandingAmountType =
      data.amountFilterType === "outstanding" ? 1 : null;

    handleReportsCountFiltering(data);
  });
  const { data: getFines, isFetching: isCountFetching } =
    ReportsCount(reportsCountModel);

  useEffect(() => {
    if (getFines?.status == 200) {
      setData(getFines?.apiData);
    }
  }, [getFines]);


  const handleClear = () => {
    accessionRef.current.clearValue();
    membercodeRef.current.clearValue();
    handleReportsCountFiltering({
      materialAccessionNoID: null,
      accessionNoValue: null,
      memberID: null,
      memberCodeValue: null,
      fromDate: null,
      toDate: null,
      admissionYearID: null,
      departmentID: null,
      instituteID: null,
      materialTypeID: null,
      memberDepartmentID: null,
      memberInstituteID: null,
      memberTypeID: null,
      reportType: null,
      amountFilterType: null,
      waivedAmountType: null,
      waivedAmount: null,
      receivedAmountType: null,
      receivedAmount: null,
      outstandingAmountType: null,
      outstandingAmount: null,
    });
  };

  useEffect(() => {
    reset(reportsCountModel);
  }, [reportsCountModel, reset]);

  const amountTypeOptions: any[] = [
    { label: "All", id: "" },
    { label: "Waived Amount", id: "waived" },
    { label: "Received Amount", id: "received" },
    { label: "Outstanding Amount", id: "outstanding" },
  ];

  const handleActualAmountExport = handleSubmit((data) => {
    setActualAmountLoading(true);
    const memberID = data.memberCodeValue ? String(data.memberCodeValue.value) : null;
    const materialAccessionNoID = data.accessionNoValue ? String(data.accessionNoValue.value) : null;
    
    const waivedAmountType = data.amountFilterType === "waived" ? 1 : null;
    const receivedAmountType = data.amountFilterType === "received" ? 1 : null;
    const outstandingAmountType = data.amountFilterType === "outstanding" ? 1 : null;

    const payload = {
      ...data,
      memberID,
      materialAccessionNoID,
      reportType: 1,
      waivedAmountType,
      receivedAmountType,
      outstandingAmountType,
      memberCodeValue: undefined,
      accessionNoValue: undefined,
      amountFilterType: undefined,
    };
    api.downloadExcel(apiURL, fileName, payload, setActualAmountLoading);
  });

  const handleOutStandingAmountExport = handleSubmit((data) => {
    setOutStandingAmountLoading(true);
    const memberID = data.memberCodeValue ? String(data.memberCodeValue.value) : null;
    const materialAccessionNoID = data.accessionNoValue ? String(data.accessionNoValue.value) : null;
    
    const waivedAmountType = data.amountFilterType === "waived" ? 1 : null;
    const receivedAmountType = data.amountFilterType === "received" ? 1 : null;
    const outstandingAmountType = data.amountFilterType === "outstanding" ? 1 : null;

    const payload = {
      ...data,
      memberID,
      materialAccessionNoID,
      reportType: 2,
      waivedAmountType,
      receivedAmountType,
      outstandingAmountType,
      memberCodeValue: undefined,
      accessionNoValue: undefined,
      amountFilterType: undefined,
    };
    api.downloadExcel(apiURL, fileName, payload, setOutStandingAmountLoading);
  });

  const handleWaivedAmountExport = handleSubmit((data) => {
    setWaivedAmountLoading(true);
    const memberID = data.memberCodeValue ? String(data.memberCodeValue.value) : null;
    const materialAccessionNoID = data.accessionNoValue ? String(data.accessionNoValue.value) : null;
    
    const waivedAmountType = data.amountFilterType === "waived" ? 1 : null;
    const receivedAmountType = data.amountFilterType === "received" ? 1 : null;
    const outstandingAmountType = data.amountFilterType === "outstanding" ? 1 : null;

    const payload = {
      ...data,
      memberID,
      materialAccessionNoID,
      reportType: 3,
      waivedAmountType,
      receivedAmountType,
      outstandingAmountType,
      memberCodeValue: undefined,
      accessionNoValue: undefined,
      amountFilterType: undefined,
    };
    api.downloadExcel(apiURL, fileName, payload, setWaivedAmountLoading);
  });

  const handleReceivedAmountExport = handleSubmit((data) => {
    setReceivedAmountLoading(true);
    const memberID = data.memberCodeValue ? String(data.memberCodeValue.value) : null;
    const materialAccessionNoID = data.accessionNoValue ? String(data.accessionNoValue.value) : null;
    
    const waivedAmountType = data.amountFilterType === "waived" ? 1 : null;
    const receivedAmountType = data.amountFilterType === "received" ? 1 : null;
    const outstandingAmountType = data.amountFilterType === "outstanding" ? 1 : null;

    const payload = {
      ...data,
      memberID,
      materialAccessionNoID,
      reportType: 4,
      waivedAmountType,
      receivedAmountType,
      outstandingAmountType,
      memberCodeValue: undefined,
      accessionNoValue: undefined,
      amountFilterType: undefined,
    };
    api.downloadExcel(apiURL, fileName, payload, setReceivedAmountLoading);
  });

  const accessionOptions: IAsyncDropdownModel[] = [];
  const handleAccessionSearch = debounce(async (query: string, callback) => {
    const options = await asyncRequests.getAccessionNo(query, null);
    callback(options.apiData || []);
  }, 500);

  const memberOptions: IAsyncDropdownModel[] = [];
  const handleMemberSearch = debounce(async (query: string, callback) => {
    const options = await asyncRequests.getMemberCode(query, null);
    callback(options.apiData || []);
  }, 500);

  const accessionRef = useRef<SelectInstance<IAsyncDropdownModel> | null>(null);
  const membercodeRef = useRef<SelectInstance<IAsyncDropdownModel> | null>(
    null,
  );

  const { data: admissionYearQuery } = GetAdmissionYearValues();
  const [admissionYearOptions, setAdmissionYearOptions] = useState<
    IDropdownModel[]
  >([]);
  useEffect(() => {
    setAdmissionYearOptions(admissionYearQuery?.apiData || []);
  }, [admissionYearQuery]);

  const { data: instituteQuery } = GetInstituteByLibrary();
  const [instituteOptions, setInstituteOptions] = useState<IDropdownModel[]>(
    [],
  );
  useEffect(() => {
    setInstituteOptions(instituteQuery?.apiData || []);
  }, [instituteQuery]);

  const instituteID = watch("instituteID");

  const { data: departmentQuery } = GetDepartmentValuesByInstitute(instituteID);
  const [departmentOptions, setDepartmentOptions] = useState<IDropdownModel[]>(
    [],
  );
  useEffect(() => {
    setDepartmentOptions(departmentQuery?.apiData || []);
  }, [departmentQuery]);

  //#region Member Institute
  const { data: memberinstituteQuery } = GetInstituteByLibrary();
  const [memberInstituteOptions, setMemberInstituteOptions] = useState<
    IDropdownModel[]
  >([]);

  useEffect(() => {
    setMemberInstituteOptions(memberinstituteQuery?.apiData || []);
  }, [memberinstituteQuery]);
  //#endregion

  //#region Member Department
  const memberInstituteID = watch("memberInstituteID");

  const { data: memberDepartmentQuery } =
    GetDepartmentValuesByInstitute(memberInstituteID);
  const [memberDepartmentOptions, setMemberDepartmentOptions] = useState<
    IDropdownModel[]
  >([]);

  useEffect(() => {
    setMemberDepartmentOptions(memberDepartmentQuery?.apiData || []);
  }, [memberDepartmentQuery]);
  //#endregion

  const { data: memberTypeQuery } = GetMemberTypeValues();
  const [memberTypeOptions, setMemberTypeOptions] = useState<IDropdownModel[]>(
    [],
  );
  useEffect(() => {
    setMemberTypeOptions(memberTypeQuery?.apiData || []);
  }, [memberTypeQuery]);

  const { data: materialTypeQuery } = GetMaterialTypeValues();
  const [materialTypeOptions, setMaterialTypeOptions] = useState<
    IDropdownModel[]
  >([]);
  useEffect(() => {
    setMaterialTypeOptions(materialTypeQuery?.apiData || []);
  }, [materialTypeQuery]);

  return (
    <>
      <Box component="form" onSubmit={onSubmit}>
        <Card elevation={3} sx={{ mb: 2 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={6} md={3} lg={3} xl={3}>
                <DatePickerElement
                  control={control}
                  name="fromDate"
                  disableFuture
                  label="From Date"
                  format="DD-MM-YYYY"
                  maxDate={
                    toDate && dayjs(toDate).isValid()
                      ? dayjs(toDate)
                      : undefined
                  }
                  sx={{ width: "100%" }}
                ></DatePickerElement>
              </Grid>
              <Grid item xs={6} md={3} lg={3} xl={3}>
                <DatePickerElement
                  control={control}
                  name="toDate"
                  disableFuture
                  label="To Date"
                  format="DD-MM-YYYY"
                  minDate={
                    fromDate && dayjs(fromDate).isValid()
                      ? dayjs(fromDate)
                      : undefined
                  }
                  sx={{ width: "100%" }}
                ></DatePickerElement>
              </Grid>
              <Grid item xs={12} sm={12} md={3} lg={3}>
                <SelectField
                  options={admissionYearOptions}
                  control={control}
                  name="admissionYearID"
                  label="Student Admission Year"
                  placeholder="Select Student Admission Year"
                />
              </Grid>
              <Grid item xs={12} sm={12} md={3} lg={3}>
                <SelectField
                  options={memberTypeOptions}
                  control={control}
                  name="memberTypeID"
                  label="Member Type"
                  placeholder="Select Member Type"
                />
              </Grid>
              <Grid item xs={12} sm={12} md={3} lg={3}>
                <SelectField
                  options={materialTypeOptions}
                  control={control}
                  name="materialTypeID"
                  label="Material Type"
                  placeholder="Select Material Type"
                />
              </Grid>
              <Grid item xs={12} md={4.5} lg={4.5} xl={4.5}>
                <Controller
                  name="accessionNoValue"
                  control={control}
                  render={({ field: { onChange, value, ref } }) => (
                    <AsyncSelect
                      ref={accessionRef}
                      blurInputOnSelect
                      defaultValue={accessionOptions}
                      styles={{
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                        control: (baseStyles) => ({
                          ...baseStyles,
                          borderColor: errors.accessionNoValue
                            ? "red"
                            : baseStyles.borderColor,
                        }),
                      }}
                      inputRef={ref}
                      getOptionLabel={(item) => item.label}
                      getOptionValue={(item) => item.value}
                      loadOptions={handleAccessionSearch}
                      noOptionsMessage={({ inputValue }) =>
                        !inputValue ? "Type Something..." : "No record found"
                      }
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      menuPlacement="bottom"
                      menuShouldScrollIntoView={false}
                      placeholder="Enter Accession No. / Title / Material Code"
                      value={value}
                      isClearable
                      onChange={(selected) => onChange(selected)}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4.5} lg={4.5} xl={4.5}>
                <Controller
                  name="memberCodeValue"
                  control={control}
                  render={({ field: { onChange, value, ref } }) => (
                    <AsyncSelect
                      ref={membercodeRef}
                      blurInputOnSelect
                      defaultValue={memberOptions}
                      styles={{
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),

                        control: (baseStyles) => ({
                          ...baseStyles,
                          borderColor: errors.memberCodeValue
                            ? "red"
                            : baseStyles.borderColor,
                        }),
                      }}
                      inputRef={ref}
                      getOptionLabel={(item) => item.label}
                      getOptionValue={(item) => item.value}
                      loadOptions={handleMemberSearch}
                      noOptionsMessage={({ inputValue }) =>
                        !inputValue ? "Type Something..." : "No record found"
                      }
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      menuPlacement="bottom"
                      menuShouldScrollIntoView={false}
                      placeholder="Enter Member Name / Member Code"
                      value={value}
                      isClearable
                      onChange={(selected) => onChange(selected)}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6} md={3} lg={3} xl={3}>
                <SelectField
                  control={control}
                  name="instituteID"
                  options={instituteOptions}
                  label="Institute"
                  placeholder="Select Institute"
                />
              </Grid>
              <Grid item xs={6} md={3} lg={3} xl={3}>
                <SelectField
                  control={control}
                  name="departmentID"
                  options={departmentOptions}
                  label="Department"
                  placeholder="Select Department"
                />
              </Grid>
              <Grid item xs={6} md={3} lg={3} xl={3}>
                <SelectField
                  control={control}
                  name="memberInstituteID"
                  options={memberInstituteOptions}
                  label="Member Institute"
                  placeholder="Select Member Institute"
                />
              </Grid>
              <Grid item xs={6} md={3} lg={3} xl={3}>
                <SelectField
                  control={control}
                  name="memberDepartmentID"
                  options={memberDepartmentOptions}
                  label="Member Department"
                  placeholder="Select Member Department"
                />
              </Grid>
              <Grid item xs={12} sm={12} md={2} lg={2}>
                <TextField
                  fullWidth
                  {...register("waivedAmount", {
                    setValueAs: (v) =>
                      v === "" || v === null || v === undefined
                        ? null
                        : Number(v),
                  })}
                  label="Waived Amount"
                  autoComplete="off"
                  placeholder="Enter Amount"
                  type="number"
                />
              </Grid>
              <Grid item xs={12} sm={12} md={2} lg={2}>
                <TextField
                  fullWidth
                  {...register("receivedAmount", {
                    setValueAs: (v) =>
                      v === "" || v === null || v === undefined
                        ? null
                        : Number(v),
                  })}
                  label="Received Amount"
                  autoComplete="off"
                  placeholder="Enter Received Amount"
                  type="number"
                />
              </Grid>
              <Grid item xs={12} sm={12} md={2} lg={2}>
                <TextField
                  fullWidth
                  {...register("outstandingAmount", {
                    setValueAs: (v) =>
                      v === "" || v === null || v === undefined
                        ? null
                        : Number(v),
                  })}
                  label="Outstanding Amount"
                  autoComplete="off"
                  placeholder="Enter Outstanding Amount"
                  type="number"
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <RadioButtonGroup
                  options={amountTypeOptions}
                  control={control}
                  name="amountFilterType"
                  label="Fine Amount Filter"
                  row
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4} lg={3} sx={{ mt: 3 }}>
                <Button variant="contained" sx={{ mr: 1 }} type="submit">
                  Search
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleClear}
                >
                  Clear
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      <Grid container direction={"row"} spacing={2}>
        <Grid item xs={12} md={12}>
          <Card elevation={3} sx={{ mb: 2 }}>
            <CardHeader
              title={<Typography variant={"h3"}>Fine Reports</Typography>}
              sx={{
                p: 2.5,
                "& .MuiCardHeader-action": {
                  m: "0px auto",
                  alignSelf: "center",
                },
              }}
            />
            <Divider sx={{ borderBottomWidth: 1.5 }} />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} lg={6} sm={12}>
                  <MainCard
                    sx={{
                      position: "relative",
                      overflow: "hidden",
                      "&:before, &:after": {
                        content: '""',
                        width: 120,
                        height: 120,
                        position: "absolute",
                        background:
                          "linear-gradient(135deg, rgba(255,255,255,0.05) 30%, rgba(255,255,255,0.2) 100%)",
                        borderRadius: "50%",
                        filter: "blur(10px)",
                      },
                    }}
                  >
                    <Grid
                      container
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Grid item>
                        <Stack spacing={1}>
                          <Typography variant="h3" color="primary.main">
                            Actual Amount
                          </Typography>
                          <Typography variant="h2" fontWeight={700}>
                            {isCountFetching ? (
                              <Skeleton variant="text" width={140} />
                            ) : data?.actualAmount ? (
                              <>
                                &#x20B9;{" "}
                                {data?.actualAmount.toLocaleString("en-IN") +
                                  ".00"}
                              </>
                            ) : (
                              <>&#x20B9; 0.00</>
                            )}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item>
                        {modules?.fineReport?.permissions?.Add ? (
                          <>
                            <IconButton
                              color="info"
                              sx={{
                                transform: "scale(1.5)",
                                transition: "all 0.3s ease-in-out",
                                "&:hover": {
                                  transform: "scale(1.7)",
                                },
                                fontSize: 20,
                              }}
                              onClick={() => handleActualAmountExport()}
                              disabled={actualAmountLoading}
                            >
                              <Tooltip title="Download Excel">
                                <CloudDownloadOutlined />
                              </Tooltip>
                            </IconButton>
                          </>
                        ) : (
                          <></>
                        )}
                      </Grid>
                    </Grid>
                  </MainCard>
                </Grid>
                <Grid item xs={12} lg={6} sm={12}>
                  <MainCard
                    sx={{
                      position: "relative",
                      overflow: "hidden",
                      "&:before, &:after": {
                        content: '""',
                        width: 120,
                        height: 120,
                        position: "absolute",
                        background:
                          "linear-gradient(135deg, rgba(255,255,255,0.05) 30%, rgba(255,255,255,0.2) 100%)",
                        borderRadius: "50%",
                        filter: "blur(10px)",
                      },
                    }}
                  >
                    <Grid
                      container
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Grid item>
                        <Stack spacing={1}>
                          <Typography variant="h3" color="error.main">
                            Oustanding Amount
                          </Typography>
                          <Typography variant="h2" fontWeight={700}>
                            {isCountFetching ? (
                              <Skeleton variant="text" width={140} />
                            ) : data?.outStandingAmount ? (
                              <>
                                &#x20B9;{" "}
                                {data?.outStandingAmount.toLocaleString(
                                  "en-IN",
                                ) + ".00"}
                              </>
                            ) : (
                              <>&#x20B9; 0.00</>
                            )}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item>
                        {" "}
                        {modules?.fineReport?.permissions?.Add ? (
                          <>
                            <IconButton
                              color="info"
                              sx={{
                                transform: "scale(1.5)",
                                transition: "all 0.3s ease-in-out",
                                "&:hover": {
                                  transform: "scale(1.7)",
                                },
                                fontSize: 20,
                              }}
                              onClick={() => handleOutStandingAmountExport()}
                              disabled={outStandingAmountLoading}
                            >
                              <Tooltip title="Download Excel">
                                <CloudDownloadOutlined />
                              </Tooltip>
                            </IconButton>
                          </>
                        ) : (
                          <></>
                        )}
                      </Grid>
                    </Grid>
                  </MainCard>
                </Grid>
                <Grid item xs={12} lg={6} sm={12}>
                  <MainCard
                    sx={{
                      position: "relative",
                      overflow: "hidden",
                      "&:before, &:after": {
                        content: '""',
                        width: 120,
                        height: 120,
                        position: "absolute",
                        background:
                          "linear-gradient(135deg, rgba(255,255,255,0.05) 30%, rgba(255,255,255,0.2) 100%)",
                        borderRadius: "50%",
                        filter: "blur(10px)",
                      },
                    }}
                  >
                    <Grid
                      container
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Grid item>
                        <Stack spacing={1}>
                          <Typography variant="h3" color="warning.main">
                            Waived Amount
                          </Typography>
                          <Typography variant="h2" fontWeight={700}>
                            {isCountFetching ? (
                              <Skeleton variant="text" width={140} />
                            ) : data?.waivedAmount ? (
                              <>
                                &#x20B9;{" "}
                                {data?.waivedAmount.toLocaleString("en-IN") +
                                  ".00"}
                              </>
                            ) : (
                              <>&#x20B9; 0.00</>
                            )}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item>
                        {modules?.fineReport?.permissions?.Add ? (
                          <>
                            <IconButton
                              color="info"
                              sx={{
                                transform: "scale(1.5)",
                                transition: "all 0.3s ease-in-out",
                                "&:hover": {
                                  transform: "scale(1.7)",
                                },
                                fontSize: 20,
                              }}
                              onClick={() => handleWaivedAmountExport()}
                              disabled={waivedAmountLoading}
                            >
                              <Tooltip title="Download Excel">
                                <CloudDownloadOutlined />
                              </Tooltip>
                            </IconButton>{" "}
                          </>
                        ) : (
                          <></>
                        )}
                      </Grid>
                    </Grid>
                  </MainCard>
                </Grid>
                <Grid item xs={12} lg={6} sm={12}>
                  <MainCard
                    sx={{
                      position: "relative",
                      overflow: "hidden",
                      "&:before, &:after": {
                        content: '""',
                        width: 120,
                        height: 120,
                        position: "absolute",
                        background:
                          "linear-gradient(135deg, rgba(255,255,255,0.05) 30%, rgba(255,255,255,0.2) 100%)",
                        borderRadius: "50%",
                        filter: "blur(10px)",
                      },
                    }}
                  >
                    <Grid
                      container
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Grid item>
                        <Stack spacing={1}>
                          <Typography
                            variant="h3"
                            color="success.dark"
                            fontWeight={600}
                          >
                            Received Amount
                          </Typography>
                          <Typography variant="h2" fontWeight={700}>
                            {isCountFetching ? (
                              <Skeleton variant="text" width={140} />
                            ) : data?.receivedAmount ? (
                              <>
                                &#x20B9;{" "}
                                {data?.receivedAmount.toLocaleString("en-IN") +
                                  ".00"}
                              </>
                            ) : (
                              <>&#x20B9; 0.00</>
                            )}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item>
                        {" "}
                        {modules?.fineReport?.permissions?.Add ? (
                          <>
                            <IconButton
                              color="info"
                              sx={{
                                transform: "scale(1.5)",
                                transition: "all 0.3s ease-in-out",
                                "&:hover": {
                                  transform: "scale(1.7)",
                                },
                                fontSize: 20,
                              }}
                              onClick={() => handleReceivedAmountExport()}
                              disabled={receivedAmountLoading}
                            >
                              <Tooltip title="Download Excel">
                                <CloudDownloadOutlined />
                              </Tooltip>
                            </IconButton>{" "}
                          </>
                        ) : (
                          <></>
                        )}
                      </Grid>
                    </Grid>
                  </MainCard>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};
export default FineReports;
